import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { AUTO_RECOVERY_DAY_OPTIONS, DASHBOARD_CUSTOMER_CONSTANTS } from "../../constants/CustomerConstants";
import { createNewCustomer, updateCustomerById } from "../../api/dashboardCustomers.Service";
import { useToastContext } from "../../context/toast";
import { validateEmptyOrNull } from "../../utilities/libs";
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';

const MuiDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

export const CustomDialog = ({
  customerData,
  setCustomerData,
  open,
  onClose,
  fields,
  isEditDialog,
  onFetchCustomers,
  loggedInUser,
  setCustomersList,
  handleResetPaginationTable,
  setSearchQuery,
  fieldErrors,
  setFieldErrors
}) => {
  const theme = useTheme();
  const CustomerId = customerData?.customerId || "";
  const { showErrorToast, showSuccessToast } = useToastContext();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCustomerData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setFieldErrors({
      ...fieldErrors,
      [name]: false,
    });
  };

  const validateFields = () => {
    // First Priority: Check mandatory fields
    const errors = {
      customerId: validateEmptyOrNull(CustomerId),
      emailAddress: validateEmptyOrNull(customerData.emailAddress),
    };

    setFieldErrors(errors);

    if (Object.values(errors).some((error) => error)) {
      showErrorToast(DASHBOARD_CUSTOMER_CONSTANTS.MANDATORY_FIELDS_VALIDATION_MESSAGE);
      return false;
    }

    // Recovery Time and Day validation
    if (!validateEmptyOrNull(customerData.recoveryTime)) {
      if (validateEmptyOrNull(customerData.recoveryDay)) {
        setFieldErrors((prevState) => ({ ...prevState, recoveryDay: true }));
        showErrorToast(DASHBOARD_CUSTOMER_CONSTANTS.DAY_FIELD_VALIDATION_MESSAGE);
        return false;
      }
    }

    if (!validateEmptyOrNull(customerData.recoveryDay)) {
      if (validateEmptyOrNull(customerData.recoveryTime)) {
        setFieldErrors((prevState) => ({ ...prevState, recoveryTime: true }));
        showErrorToast(DASHBOARD_CUSTOMER_CONSTANTS.HOURS_FIELDS_VALIDATION_MESSAGE);
        return false;
      }
    }

    // Fixed validation logic for receiving hours
    if (!validateEmptyOrNull(customerData.startReceivingHours)) {
      if (validateEmptyOrNull(customerData.endRecevingHours)) {
        setFieldErrors((prevState) => ({ ...prevState, endRecevingHours: true }));
        showErrorToast(DASHBOARD_CUSTOMER_CONSTANTS.END_TIME_FIELDS_VALIDATION_MESSAGE);
        return false;
      }
    }

    if (!validateEmptyOrNull(customerData.endRecevingHours)) {
      if (validateEmptyOrNull(customerData.startReceivingHours)) {
        setFieldErrors((prevState) => ({ ...prevState, startReceivingHours: true }));
        showErrorToast(DASHBOARD_CUSTOMER_CONSTANTS.START_TIME_FIELDS_VALIDATION_MESSAGE);
        return false;
      }
    }

    return true;
  }

  const handleSave = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (!validateFields()) {
      return;
    }

    const payload = {
      customerId: CustomerId,
      phoneNumber: customerData.phoneNumber,
      warehouse: customerData.warehouse,
      emailAddress: customerData.emailAddress,
      recoveryTime: customerData.recoveryTime,
      recoveryDay: customerData.recoveryDay,
      startReceivingHours: customerData.startReceivingHours,
      endRecevingHours: customerData.endRecevingHours,
    }


    const response = isEditDialog
      ? await updateCustomerById(CustomerId, payload)
      : await createNewCustomer(payload);

    const { isSuccessful, message, data } = response;

    if (!isSuccessful) {
      showErrorToast(message);
      if (message.toLowerCase() === DASHBOARD_CUSTOMER_CONSTANTS.CUSTOMER_ID_EXISTS_ERROR.toLowerCase()) {
        setFieldErrors((prevState) => ({ ...prevState, customerId: true }));
      }
      return;
    }

    // Update the customers list
    if (isEditDialog) {
      setCustomersList((prevList) => prevList.map(customer =>
        customer.customerId === CustomerId ? data : customer))
    } else {
      setSearchQuery('')
      handleResetPaginationTable()
      onFetchCustomers(true);
    }

    onClose();
    showSuccessToast(message);
  };

  // Render different input types based on field property
  const renderField = (field) => {
    const { property, label, isEditable } = field;
    const value = customerData[property] || '';

    if (property === 'recoveryDay') {
      return (
        <ToggleButtonGroup
          value={value}
          exclusive
          onChange={(event, newValue) => {
            if (newValue !== null) {  // Prevent unselecting all buttons
              setCustomerData((prevState) => ({
                ...prevState,
                [property]: newValue,
              }));
              setFieldErrors({
                ...fieldErrors,
                [property]: false,
              });
            }
          }}
          error={fieldErrors[property]}
          name={property}
          disabled={!isEditable}
          fullWidth
          sx={{
            border: fieldErrors[property] ? `1px solid #FF0000` : '1px solid transparent',
            borderRadius: '4px',
          }}
        >
          {AUTO_RECOVERY_DAY_OPTIONS.map((option) => (
            <ToggleButton
              key={option.value}
              value={option.value}
              sx={{
                '&.Mui-selected': {
                  color: theme.palette.text.buttonhover,
                  bgcolor: theme.palette.button.main,
                  "&:hover": {
                    backgroundColor: theme.palette.buttonhover.main,
                  },
                },
                ...(fieldErrors[property] && {
                  border: `1px solid #FF0000`,
                }),
              }}
            >
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      );
    }

    if (property === 'recoveryTime') {
      const parsedValue = customerData[property] ? dayjs(customerData[property], 'HH:mm') : null;

      return (
        <LocalizationProvider key={property} dateAdapter={AdapterDayjs}>
          <DemoContainer components={['TimePicker']}>
            <TimePicker
              label={label}
              value={parsedValue}
              onChange={(newValue) => {
                const formattedTime = newValue ? newValue.format('HH:mm') : null;
                setCustomerData((prevState) => ({
                  ...prevState,
                  [property]: formattedTime,
                }));
                setFieldErrors({
                  ...fieldErrors,
                  [property]: false,
                });
              }}
              disabled={!isEditable}
              slotProps={{
                textField: {
                  error: fieldErrors[property],
                  sx: {
                    width: '100%',
                    '& .MuiInputLabel-root': {
                      color: fieldErrors[property] ? '#FF0000' : theme.palette.text.primary,
                      '&.Mui-focused': {
                        color: fieldErrors[property] ? '#FF0000' : theme.palette.FocusStyle.main,
                      },
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: fieldErrors[property] ? '#FF0000' : theme.palette.border.main,
                      },
                      '&:hover fieldset': {
                        borderColor: fieldErrors[property] ? '#FF0000' : theme.palette.border.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: fieldErrors[property] ? '#FF0000' : theme.palette.FocusStyle.main,
                      },
                    },
                    '& .MuiInputAdornment-root svg': {
                      color: fieldErrors[property] ? '#FF0000' : 'inherit',
                    },
                  }
                }
              }}
            />
          </DemoContainer>
        </LocalizationProvider>
      );
    }

    if (property === 'startReceivingHours' || property === 'endRecevingHours') {
      const parsedValue = customerData[property] ? dayjs(customerData[property], 'HH:mm') : null;

      return (
        <LocalizationProvider key={property} dateAdapter={AdapterDayjs}>
          <DemoContainer components={['TimePicker']}>
            <TimePicker
              label={label}
              value={parsedValue}
              onChange={(newValue) => {
                const formattedTime = newValue ? newValue.format('HH:mm') : null;
                setCustomerData((prevState) => ({
                  ...prevState,
                  [property]: formattedTime,
                }));
                setFieldErrors({
                  ...fieldErrors,
                  [property]: false,
                });
              }}
              disabled={!isEditable}
              slotProps={{
                textField: {
                  error: fieldErrors[property],
                  sx: {
                    width: '100%',
                    '& .MuiInputLabel-root': {
                      color: fieldErrors[property] ? '#FF0000' : theme.palette.text.primary,
                      '&.Mui-focused': {
                        color: fieldErrors[property] ? '#FF0000' : theme.palette.FocusStyle.main,
                      },
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: fieldErrors[property] ? '#FF0000' : theme.palette.border.main,
                      },
                      '&:hover fieldset': {
                        borderColor: fieldErrors[property] ? '#FF0000' : theme.palette.border.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: fieldErrors[property] ? '#FF0000' : theme.palette.FocusStyle.main,
                      },
                    },
                    '& .MuiInputAdornment-root svg': {
                      color: fieldErrors[property] ? '#FF0000' : 'inherit',
                    },
                  }
                }
              }}
            />
          </DemoContainer>
        </LocalizationProvider>
      );
    }

    // Default TextField for other fields
    return (
      <TextField
        key={property}
        name={property}
        label={label}
        value={value}
        error={fieldErrors[property]}
        sx={{ marginTop: '0px' }}
        onChange={handleChange}
        autoComplete="off"
        fullWidth
        margin="normal"
        variant="outlined"
        InputProps={{
          readOnly: !isEditable,
        }}
        InputLabelProps={{
          sx: {
            color: theme.palette.text.primary,
            "&.Mui-focused": {
              color: theme.palette.FocusStyle.main,
            },
          },
        }}
      />
    );
  };

  // Filter fields excluding customerId if isEditDialog is true
  const filteredFields = fields.filter((field) => !isEditDialog || field.property !== 'customerId');

  // Extract startReceivingHours and endRecevingHours fields
  const startTimeField = filteredFields.find(field => field.property === 'startReceivingHours');
  const endTimeField = filteredFields.find(field => field.property === 'endRecevingHours');

  return (
    <MuiDialog onClose={onClose} aria-labelledby='customized-dialog-title' open={open}>
      <DialogTitle sx={{ m: 0, p: 2, fontWeight: '600' }} id='customized-dialog-title'>
        {isEditDialog
          ? DASHBOARD_CUSTOMER_CONSTANTS.EDIT_CUSTOMER
          : DASHBOARD_CUSTOMER_CONSTANTS.ADD_CUSTOMER}
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxWidth: "500px !important", maxHeight: "300px !important" }}>
        <Box
          sx={{
            padding: "0px",
            margin: "0px",
          }}
        >
          {isEditDialog && (
            <Typography
              sx={{
                padding: "0px",
                marginBottom: "15px",
              }}
              variant='body1'
            >
              Customer ID: {CustomerId}
            </Typography>
          )}

          <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
            {filteredFields.map((field) => {
              // Skip rendering startReceivingHours and endRecevingHours fields here because handled separately
              if (field.property === 'startReceivingHours' || field.property === 'endRecevingHours') {
                return null;
              }

              if (field.property === 'recoveryTime') {
                return (
                  <Box
                    key={field.property}
                    sx={{ display: 'flex', flexDirection: "column", width: "100%", flex: '1 1 100%' }}
                  >
                    <Typography
                      sx={{
                        padding: "0px",
                        margin: "0px",
                        fontWeight: '700'
                      }}
                      variant='body1'
                    >
                      Auto Recovery
                    </Typography>
                    {renderField(field)}
                  </Box>
                );
              }

              return (
                <Box key={field.property} sx={{ flex: '1 1 100%' }}>
                  {renderField(field)}
                </Box>
              );
            })}

            {(startTimeField && endTimeField) && (
              <Box sx={{ flex: '1 1 100%' }}>
                <Typography
                  sx={{
                    padding: "0px",
                    margin: "0px",
                    fontWeight: '700'
                  }}
                  variant='body1'
                >
                  Hours of Receiving
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: "row", width: "100%", justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ flex: '1 1 48%' }}>
                    {renderField(startTimeField)}
                  </Box>
                  <Box sx={{ flex: '1 1 48%' }}>
                    {renderField(endTimeField)}
                  </Box>
                </Box>
              </Box>
            )}

          </Box>

        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "0px", margin: "5px" }}>
        <Button
          autoFocus
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontFamily: "Poppins, sans-serif",
            color: theme.palette.text.primary,
            height: "40px",
            borderRadius: "10px",
            padding: "10px",
            width: "6vw",
          }}
        >
          Discard
        </Button>
        <Button
          btnVariant='contained'
          autoFocus
          onClick={handleSave}
          sx={{
            textTransform: "none",
            fontFamily: "Poppins, sans-serif",
            color: theme.palette.text.buttonhover,
            backgroundColor: theme.palette.button.main,
            height: "35px",
            "&:hover": {
              backgroundColor: theme.palette.buttonhover.main,
            },
            borderRadius: "15px",
            marginLeft: "20px !important",
            padding: "16px",
            width: "6vw",
          }}
        >
          {isEditDialog ? "Save" : "Submit"}
        </Button>
      </DialogActions>
    </MuiDialog>
  );
};
